import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Paper,
  Grid,
  Alert,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Collapse,
  IconButton,
  Checkbox,
} from "@mui/material";
import AddPhotoAlternateRoundedIcon from "@mui/icons-material/AddPhotoAlternateRounded";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useMutation } from "react-query";
import { useAuthContext } from "../context/AuthContext";
import { useS3Upload } from "../hooks/useS3Upload";
import { fetchData, generateBannerDraftWithAi } from "../api/client";

const toInt = (v) => (v === "" || v == null ? null : Math.trunc(Number(v)));
const genId = () =>
  typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `tmp_${Date.now()}_${Math.random().toString(16).slice(2)}`;

const MAX_FILE_MB = 10;
const MAX_AI_VISUAL_REFS = 4;

const base64ToFile = (base64, filename, mimeType = "image/png") => {
  const binary = atob(base64);
  const length = binary.length;
  const bytes = new Uint8Array(length);
  for (let i = 0; i < length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return new File([bytes], filename, { type: mimeType });
};

// tamanhos recomendados 
const HERO_SIZE_HINT =
  "Banner principal (carousel): 1920 x 600 px (proporção ~16:5).";

const HERO_SIMPLE_SIZE_HINT =
  "Hero simples: 1920 x 600 px (proporção ~16:5), imagem ocupando toda a largura.";

const CARD_SIZE_HINT =
  "Cards: use 4:3 (ex.: 1200 x 900 px) para os cards grandes e 8:3 (ex.: 1200 x 450 px) para os cards horizontais.";

const parseRatioCss = (ratio) => {
  if (!ratio) return "4 / 3";
  const text = String(ratio).trim();
  const sep = text.includes("/") ? "/" : text.includes(":") ? ":" : null;
  if (!sep) return "4 / 3";
  const [a, b] = text.split(sep).map((v) => v.trim());
  if (!a || !b) return "4 / 3";
  return `${a} / ${b}`;
};

const parseRatioNumber = (ratio) => {
  const text = String(ratio || "").trim();
  const sep = text.includes("/") ? "/" : text.includes(":") ? ":" : null;
  if (!sep) return null;
  const [aRaw, bRaw] = text.split(sep);
  const a = Number(aRaw);
  const b = Number(bRaw);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a <= 0 || b <= 0) return null;
  return a / b;
};

const gcd = (a, b) => {
  let x = Math.abs(Math.trunc(a));
  let y = Math.abs(Math.trunc(b));
  while (y) {
    const t = y;
    y = x % y;
    x = t;
  }
  return x || 1;
};

const formatAspectRatioLabel = (width, height) => {
  const w = Math.max(1, Math.trunc(Number(width) || 1));
  const h = Math.max(1, Math.trunc(Number(height) || 1));
  const g = gcd(w, h);
  return `${w / g}:${h / g}`;
};

const clampAiDimension = (value, min, max, fallback) => {
  const n = Math.trunc(Number(value));
  if (!Number.isFinite(n) || n <= 0) return fallback;
  return Math.min(max, Math.max(min, n));
};

const defaultOutputDimensionsForTipo = (tipo, ratio) => {
  if (tipo === "card") {
    const parsed = parseRatioNumber(ratio || "1175/440");
    const width = 1200;
    const height = parsed ? Math.max(1, Math.round(width / parsed)) : 900;
    return { width, height };
  }
  return { width: 1920, height: 600 };
};

const resolvePreviewAspectRatio = (banner, { isCard, isWideHero }) => {
  if (banner?.outputWidth && banner?.outputHeight) {
    return `${banner.outputWidth} / ${banner.outputHeight}`;
  }
  if (isCard) return parseRatioCss(banner?.ratio);
  if (isWideHero) return "16 / 5";
  return undefined;
};

const Preview = ({ banner }) => {
  const isCard = banner?.tipo === "card";
  const isSimpleHero = banner?.tipo === "heroImage";
  const isWideHero = isSimpleHero || banner?.tipo === "carousel";
  const previewSrc = banner?.imagePreview || banner?.imageSrc;

  const hasOverlayText = !isCard && !isSimpleHero;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        p: 1,
        overflow: "hidden",
        bgcolor:
          banner?.background || (isCard ? "background.default" : "#0b1635"),
      }}
    >
      <Box
        sx={{
          position: "relative",
          height: isCard ? "auto" : isWideHero ? "auto" : 260,
          aspectRatio: resolvePreviewAspectRatio(banner, { isCard, isWideHero }),
          borderRadius: 1.5,
          overflow: "hidden",
          bgcolor: "#1115",
          cursor: isSimpleHero ? "pointer" : "default",
        }}
      >
        {previewSrc && (
          <Box
            component="img"
            src={previewSrc}
            alt={banner.imageAlt || ""}
            sx={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: isWideHero || isCard ? "cover" : "contain",
              objectPosition: "center",
              opacity: 0.98,
            }}
          />
        )}

        {hasOverlayText && (
          <Box
            sx={{
              position: "absolute",
              top: 16,
              left: 16,
              right: 16,
              color: "white",
            }}
          >
            {banner?.eyebrow && (
              <Box
                sx={{
                  display: "inline-flex",
                  px: 1,
                  py: 0.5,
                  fontSize: 12,
                  borderRadius: 999,
                  bgcolor: "rgba(255,255,255,.18)",
                  backdropFilter: "blur(4px)",
                }}
              >
                {banner.eyebrow}
              </Box>
            )}
            {banner?.title && (
              <Typography
                variant="h5"
                fontWeight={800}
                sx={{
                  mt: 1,
                  textShadow: "0 1px 0 rgba(0,0,0,.2)",
                }}
              >
                {banner.title}
              </Typography>
            )}
            {banner?.subtitle && (
              <Typography variant="body2" sx={{ mt: 0.5, opacity: 0.9 }}>
                {banner.subtitle}
              </Typography>
            )}
            {banner?.ctaLabel && (
              <Box sx={{ mt: 1.5 }}>
                <Button variant="contained" size="small">
                  {banner.ctaLabel}
                </Button>
              </Box>
            )}
          </Box>
        )}

        {isSimpleHero && !previewSrc && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "white",
              fontSize: 14,
              px: 2,
              textAlign: "center",
            }}
          >
            Banner simples (imagem clicável). Selecione uma imagem e informe o
            link.
          </Box>
        )}
      </Box>
    </Box>
  );
};

const AddBannerForm = ({ bannerData, onClose, onSubmit, isLoading }) => {
  const { userData } = useAuthContext();
  const { uploadOne } = useS3Upload();
  const [errorMessage, setErrorMessage] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadInfo, setUploadInfo] = useState(null); // exibe nome do arquivo durante upload
  const [aiDialogOpen, setAiDialogOpen] = useState(false);
  const [aiBrief, setAiBrief] = useState("");
  const [aiNotice, setAiNotice] = useState("");
  const [aiProgress, setAiProgress] = useState("");
  const [aiErrorMessage, setAiErrorMessage] = useState(null);
  const [aiAdvancedOpen, setAiAdvancedOpen] = useState(false);
  const [aiReferenceAssets, setAiReferenceAssets] = useState([]);
  const [aiStyleExamples, setAiStyleExamples] = useState([]);
  const [aiUseWorkspaceBanners, setAiUseWorkspaceBanners] = useState(false);
  const [aiWorkspaceBannerIds, setAiWorkspaceBannerIds] = useState([]);
  const [aiOutputWidth, setAiOutputWidth] = useState("1920");
  const [aiOutputHeight, setAiOutputHeight] = useState("600");
  const [workspaceBanners, setWorkspaceBanners] = useState([]);
  const fileRef = useRef(null);
  const aiAssetRef = useRef(null);
  const aiStyleRef = useRef(null);

  // tipo vem do backend ou cai no default "heroImage" (hero simples)
  const initialTipo =
    bannerData?.tipo === "card" ||
    bannerData?.tipo === "heroImage" ||
    bannerData?.tipo === "carousel"
      ? bannerData.tipo
      : "heroImage";

  const [form, setForm] = useState({
    ...bannerData,
    id_usuario: bannerData?.id_usuario || userData?.id,
    id_usuario_ultima_alteracao: userData?.id,
    nome: bannerData?.nome || "",
    tipo: initialTipo, // 'carousel' | 'heroImage' | 'card'
    ordem: bannerData?.ordem ?? "",
    status: bannerData?.status == null ? 1 : Number(bannerData.status),

    eyebrow: bannerData?.eyebrow || "",
    title: bannerData?.title || "",
    subtitle: bannerData?.subtitle || "",
    ctaLabel: bannerData?.ctaLabel || "",
    ctaHref: bannerData?.ctaHref || "",
    imageSrc: bannerData?.imageSrc || "",
    imageAlt: bannerData?.imageAlt || "",
    background: bannerData?.background || "#98999a",

    ratio: bannerData?.ratio || "1175/440", // apenas 'card'
    fit: bannerData?.fit || "cover", // apenas 'card'

    imagePreview: null, // preview local
    imageFile: null, // arquivo selecionado (para upload real)
  });

  useEffect(() => {
    const nextTipo =
      bannerData?.tipo === "card" ||
      bannerData?.tipo === "heroImage" ||
      bannerData?.tipo === "carousel"
        ? bannerData.tipo
        : "heroImage";

    setForm((p) => ({
      ...p,
      ...bannerData,
      id_usuario: bannerData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
      nome: bannerData?.nome || "",
      tipo: nextTipo,
      ordem: bannerData?.ordem ?? "",
      status: bannerData?.status == null ? 1 : Number(bannerData.status),
      eyebrow: bannerData?.eyebrow || "",
      title: bannerData?.title || "",
      subtitle: bannerData?.subtitle || "",
      ctaLabel: bannerData?.ctaLabel || "",
      ctaHref: bannerData?.ctaHref || "",
      imageSrc: bannerData?.imageSrc || "",
      imageAlt: bannerData?.imageAlt || "",
      background: bannerData?.background || "#98999a",
      ratio: bannerData?.ratio || "1175/440",
      fit: bannerData?.fit || "cover",
      imagePreview: null,
      imageFile: null,
    }));
    setAiDialogOpen(false);
    setAiBrief("");
    setAiNotice("");
    setAiProgress("");
    setAiErrorMessage(null);
    setAiAdvancedOpen(false);
    setAiReferenceAssets([]);
    setAiStyleExamples([]);
    setAiUseWorkspaceBanners(false);
    setAiWorkspaceBannerIds([]);
    const dims = defaultOutputDimensionsForTipo(nextTipo, bannerData?.ratio || "1175/440");
    setAiOutputWidth(String(dims.width));
    setAiOutputHeight(String(dims.height));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerData?.id]);

  useEffect(() => {
    if (!aiDialogOpen) return;
    const dims = defaultOutputDimensionsForTipo(form.tipo, form.ratio);
    setAiOutputWidth(String(dims.width));
    setAiOutputHeight(String(dims.height));
  }, [aiDialogOpen, form.tipo, form.ratio]);

  const aiOutputPreview = useMemo(() => {
    const width = clampAiDimension(aiOutputWidth, 400, 3840, 1920);
    const height = clampAiDimension(aiOutputHeight, 100, 2160, 600);
    return {
      width,
      height,
      aspectRatio: formatAspectRatioLabel(width, height),
      ratio: `${width}/${height}`,
      label: `${width}×${height} px`,
    };
  }, [aiOutputWidth, aiOutputHeight]);

  useEffect(() => {
    if (!aiDialogOpen) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetchData("/banners?page=1&limit=40");
        const payload = res?.data ?? res;
        const items = Array.isArray(payload?.items)
          ? payload.items
          : Array.isArray(payload?.rows)
          ? payload.rows
          : Array.isArray(payload)
          ? payload
          : [];
        const withImage = items.filter(
          (b) => b?.imageSrc && /^https?:\/\//i.test(String(b.imageSrc))
        );
        if (!cancelled) setWorkspaceBanners(withImage);
      } catch {
        if (!cancelled) setWorkspaceBanners([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [aiDialogOpen]);

  const buildAiRefPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/banners/ai-refs/${yyyy}/${mm}`;
  };

  const addAiVisualFiles = (fileList, kind) => {
    const list = Array.from(fileList || []);
    if (!list.length) return;
    const nextItems = list.slice(0, MAX_AI_VISUAL_REFS).map((file) => ({
      id: genId(),
      file,
      label: file.name.replace(/\.[^.]+$/, ""),
      preview: URL.createObjectURL(file),
    }));
    if (kind === "asset") {
      setAiReferenceAssets((prev) => [...prev, ...nextItems].slice(0, MAX_AI_VISUAL_REFS));
    } else {
      setAiStyleExamples((prev) => [...prev, ...nextItems].slice(0, MAX_AI_VISUAL_REFS));
    }
  };

  const removeAiVisualItem = (kind, id) => {
    const updater = (prev) => {
      const target = prev.find((item) => item.id === id);
      if (target?.preview) URL.revokeObjectURL(target.preview);
      return prev.filter((item) => item.id !== id);
    };
    if (kind === "asset") setAiReferenceAssets(updater);
    else setAiStyleExamples(updater);
  };

  const toggleWorkspaceBannerId = (bannerId) => {
    setAiWorkspaceBannerIds((prev) => {
      const id = Number(bannerId);
      if (prev.includes(id)) return prev.filter((v) => v !== id);
      if (prev.length >= MAX_AI_VISUAL_REFS) return prev;
      return [...prev, id];
    });
  };

  const uploadAiVisualItems = async (items) => {
    const prefix = buildAiRefPrefix();
    const uploaded = [];
    for (const item of items) {
      if (item.url) {
        uploaded.push({ url: item.url, label: item.label });
        continue;
      }
      if (!item.file) continue;
      const { url } = await uploadOne(item.file, { prefix });
      uploaded.push({
        url,
        label: item.label || item.file.name,
      });
    }
    return uploaded;
  };

  const aiDraftMutation = useMutation(
    async () => {
      const hasRefs =
        aiReferenceAssets.length > 0 ||
        aiStyleExamples.length > 0 ||
        aiUseWorkspaceBanners ||
        aiWorkspaceBannerIds.length > 0;

      let aiOptions = {
        outputWidth: aiOutputPreview.width,
        outputHeight: aiOutputPreview.height,
      };
      if (hasRefs) {
        setAiProgress("Enviando imagens de referência…");
        const referenceAssets = await uploadAiVisualItems(aiReferenceAssets);
        const styleExamples = await uploadAiVisualItems(aiStyleExamples);
        aiOptions = {
          ...aiOptions,
          referenceAssets,
          styleExamples,
          useWorkspaceBannersAsStyle: aiUseWorkspaceBanners,
          workspaceBannerIds: aiWorkspaceBannerIds,
        };
      }

      return generateBannerDraftWithAi(
        {
          brief: aiBrief,
          tipo: form.tipo,
          existing: {
            nome: form.nome,
            eyebrow: form.eyebrow,
            title: form.title,
            subtitle: form.subtitle,
            ctaLabel: form.ctaLabel,
            ctaHref: form.ctaHref,
            background: form.background,
            ratio: form.ratio,
          },
          aiOptions,
        },
        {
          onStatus: (_status, job) => {
            const logs = Array.isArray(job?.log) ? job.log : [];
            const lastLog = logs[logs.length - 1];
            if (lastLog) setAiProgress(lastLog);
          },
        }
      );
    },
    {
      onSuccess: (data) => {
        const draft = data?.draft || {};
        const image = data?.image || {};
        const quality = image?.quality || {};
        const imageRejected = image?.rejected === true || quality?.rejectDelivery === true;

        const nextFile =
          !imageRejected && image?.base64 && image?.suggestedFileName
            ? base64ToFile(
                image.base64,
                image.suggestedFileName,
                image.mimeType || "image/png"
              )
            : null;
        const preview = nextFile ? URL.createObjectURL(nextFile) : null;

        setForm((prev) => ({
          ...prev,
          tipo: draft?.tipo || prev.tipo,
          nome: draft?.nome || prev.nome,
          eyebrow: draft?.eyebrow ?? prev.eyebrow,
          title: draft?.title ?? prev.title,
          subtitle: draft?.subtitle ?? prev.subtitle,
          ctaLabel: draft?.ctaLabel ?? prev.ctaLabel,
          imageAlt: draft?.imageAlt || prev.imageAlt,
          background: draft?.background || prev.background,
          ratio: image?.ratio || prev.ratio,
          outputWidth: image?.outputWidth || prev.outputWidth,
          outputHeight: image?.outputHeight || prev.outputHeight,
          ...(nextFile
            ? { imagePreview: preview, imageFile: nextFile }
            : {}),
        }));

        if (imageRejected) {
          const rejectionSummary =
            quality.summary ||
            quality.qualityWarning ||
            "A imagem não passou na revisão de design automática.";
          setAiErrorMessage(
            `${rejectionSummary} Os campos de texto foram preenchidos — ajuste o briefing ou tente gerar novamente.`
          );
          setAiNotice(
            draft?.intentSummary
              ? `Entendemos: ${draft.intentSummary}. Revise o pedido e clique em Gerar novamente.`
              : "Revise o pedido e clique em Gerar novamente."
          );
          setAiProgress("");
          return;
        }

        let notice =
          draft?.intentSummary
            ? `Entendemos: ${draft.intentSummary}. A IA preencheu o formulário e aplicou a imagem no preview.`
            : "A IA preencheu o formulário com um rascunho e aplicou a imagem no preview.";

        if (quality.approved) {
          if (quality.attempts > 1) {
            notice += ` Aprovado na revisão de design (tentativa ${quality.attempts}/${quality.maxAttempts}).`;
          } else if (quality.qualityScore != null) {
            notice += ` Revisão de design: nota ${quality.qualityScore}/100.`;
          }
        }

        notice += " Revise tudo antes de salvar.";
        setAiNotice(notice);
        setErrorMessage(null);
        setAiDialogOpen(false);
        setAiBrief("");
        setAiProgress("");
        setAiErrorMessage(null);
        setAiAdvancedOpen(false);
        setAiReferenceAssets([]);
        setAiStyleExamples([]);
        setAiUseWorkspaceBanners(false);
        setAiWorkspaceBannerIds([]);
      },
      onError: (err) => {
        setAiProgress("");
        setAiErrorMessage(
          err?.response?.data?.message ||
            err?.job?.errorMessage ||
            err?.message ||
            "Não foi possível gerar o banner com IA agora."
        );
      },
    }
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAiNotice("");

    // quando troca o tipo, limpamos o que não faz sentido
    if (name === "tipo") {
      const nextTipo = value;
      setForm((p) => ({
        ...p,
        tipo: nextTipo,
        ...(nextTipo === "heroImage"
          ? {
              eyebrow: "",
              title: "",
              subtitle: "",
              ctaLabel: "",
              background: "#0b1635",
            }
          : {}),
      }));
      return;
    }

    setForm((p) => ({ ...p, [name]: value }));
  };

  const pickFile = () => fileRef.current?.click();

  const validateImage = (file) => {
    if (!file) return "Arquivo inválido.";
    const isImage = /^image\//i.test(file.type);
    if (!isImage) return "Selecione uma imagem válida.";
    const sizeOk = file.size <= MAX_FILE_MB * 1024 * 1024;
    if (!sizeOk) return `Imagem maior que ${MAX_FILE_MB}MB.`;
    return null;
  };

  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const err = validateImage(f);
    if (err) {
      setErrorMessage(err);
      setTimeout(() => setErrorMessage(null), 4000);
      return;
    }
    setAiNotice("");
    const preview = URL.createObjectURL(f);
    setForm((p) => ({ ...p, imagePreview: preview, imageFile: f }));
  };

  const buildPrefix = () => {
    const d = new Date();
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const workspace = userData?.id_workspace || "default";
    return `workspace-${workspace}/banners/${yyyy}/${mm}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(null);

    if (!(form.nome || form.title)) {
      setErrorMessage("Informe pelo menos 'Nome' (interno) ou 'Título'.");
      return;
    }

    // Upload real se houver novo arquivo
    let finalImageUrl = form.imageSrc || "";
    try {
      if (form.imageFile) {
        setIsUploading(true);
        setUploadInfo(form.imageFile.name);
        const { url } = await uploadOne(form.imageFile, {
          prefix: buildPrefix(),
        });
        if (!url) throw new Error("Falha ao enviar imagem para o S3.");
        finalImageUrl = url;
      }
    } catch (err) {
      setErrorMessage(err?.message || "Erro ao enviar imagem para o S3.");
      setIsUploading(false);
      setUploadInfo(null);
      return;
    } finally {
      setIsUploading(false);
      setUploadInfo(null);
    }

    const payload = {
      id: form.id,
      nome: (form.nome || form.title || "").trim(),
      tipo: form.tipo, // sempre o valor do select
      ordem: toInt(form.ordem),
      status: Number(form.status) === 1 ? 1 : 0,

      eyebrow: form.tipo === "heroImage" ? "" : form.eyebrow || "",
      title: form.tipo === "heroImage" ? "" : form.title || "",
      subtitle: form.tipo === "heroImage" ? "" : form.subtitle || "",
      ctaLabel:
        form.tipo === "heroImage" ? "" : form.ctaLabel ? form.ctaLabel : "",
      ctaHref: form.ctaHref || "",
      imageSrc: finalImageUrl,
      imageAlt: form.imageAlt || "",
      background:
        form.tipo === "heroImage" ? "" : form.background ? form.background : "",

      ratio: form.ratio || "1175/440",
      fit: form.fit || "cover",

      isNewImage: !!form.imageFile,
      localTmpId: !form.id ? genId() : undefined,

      id_usuario: bannerData?.id_usuario || userData?.id,
      id_usuario_ultima_alteracao: userData?.id,
    };

    try {
      await onSubmit(payload);
    } catch (err) {
      setErrorMessage(
        err?.response?.data?.message || "Erro ao salvar o banner."
      );
    }
  };

  const isCard = form.tipo === "card";
  const isSimpleHero = form.tipo === "heroImage";
  const isGeneratingAi = aiDraftMutation.isLoading;
  const isBusy = isLoading || isUploading || isGeneratingAi;

  let sizeHint = HERO_SIZE_HINT;
  if (isSimpleHero) sizeHint = HERO_SIMPLE_SIZE_HINT;
  if (isCard) sizeHint = CARD_SIZE_HINT;

  const aiTypeHint =
    form.tipo === "heroImage"
      ? "Hero simples: a IA interpreta seu pedido, define a copy na arte (headline, apoio e CTA) e gera uma imagem 16:5 pronta para uso."
      : form.tipo === "card"
      ? "Card: a IA interpreta a intenção, gera a imagem visual do grid (sem texto na arte) e sugere um CTA curto no formulário."
      : "Carousel: a IA interpreta o briefing, cria o fundo visual 16:5 (zona esquerda livre) e preenche título, subtítulo e CTA para sobreposição HTML.";

  const aiPlaceholder =
    form.tipo === "heroImage"
      ? "Ex.: Banner premium para uma promoção de lançamento. Público: clientes da loja. Tom moderno e confiável. Headline direta, apoio curto e CTA claro (ex.: Saiba mais)."
      : form.tipo === "card"
      ? "Ex.: Card promocional com produto em destaque. Público: visitantes do site. Visual limpo, alto contraste e sensação de oportunidade."
      : "Ex.: Campanha com oferta por tempo limitado. Assunto visual à direita, zona esquerda escura para textos. Título impactante, subtítulo com benefício e CTA (ex.: Aproveitar).";

  return (
    <Paper elevation={3} sx={{ p: 3, width: "95%", m: "0 auto" }}>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 1.5,
          mb: 2,
        }}
      >
        <Typography variant="h6">
          {form?.id ? "Editar Banner" : "Adicionar Banner"}
        </Typography>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<AutoAwesomeIcon />}
          onClick={() => {
            setAiErrorMessage(null);
            setAiDialogOpen(true);
          }}
          disabled={isBusy}
        >
          {isGeneratingAi ? "Gerando com IA..." : "Gerar com IA"}
        </Button>
      </Box>

      {(errorMessage || isUploading || aiNotice) && !aiDialogOpen && (
        <Box sx={{ mb: 2 }}>
          {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
          {aiNotice && (
            <Alert severity="success" sx={{ mb: errorMessage ? 1 : 0 }}>
              {aiNotice}
            </Alert>
          )}
          {isUploading && (
            <Alert severity="info">
              {uploadInfo ? `Enviando imagem: ${uploadInfo}` : "Enviando imagem..."}
            </Alert>
          )}
        </Box>
      )}

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2}>
          {/* Coluna esquerda (campos) */}
          <Grid item xs={12} md={7.5}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={8}>
                <TextField
                  label="Nome (interno)"
                  name="nome"
                  value={form.nome}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Ordem"
                  name="ordem"
                  value={form.ordem ?? ""}
                  onChange={handleChange}
                  inputMode="numeric"
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    name="tipo"
                    value={form.tipo}
                    onChange={handleChange}
                  >
                    <MenuItem value="carousel">
                      Carousel (hero fragmentado)
                    </MenuItem>
                    <MenuItem value="heroImage">
                      Hero simples (imagem clicável)
                    </MenuItem>
                    <MenuItem value="card">Card (grid)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid
                item
                xs={12}
                md={4}
                sx={{ display: "flex", alignItems: "center" }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={Number(form.status) === 1}
                      onChange={(e) =>
                        setForm((p) => ({
                          ...p,
                          status: e.target.checked ? 1 : 0,
                        }))
                      }
                    />
                  }
                  label={Number(form.status) === 1 ? "Ativo" : "Inativo"}
                />
              </Grid>

              {/* Campos de conteúdo só para hero fragmentado (carousel) */}
              {!isCard && !isSimpleHero && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Texto de destaque (acima do título)"
                      name="eyebrow"
                      value={form.eyebrow}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Título"
                      name="title"
                      value={form.title}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      label="Subtítulo"
                      name="subtitle"
                      value={form.subtitle}
                      onChange={handleChange}
                      fullWidth
                      multiline
                      minRows={2}
                    />
                  </Grid>
                </>
              )}

              {/* CTA – no hero simples só usamos o link (sem label/botão) */}
              {!isSimpleHero && (
                <Grid item xs={12} md={6}>
                  <TextField
                    label="TEXTO DO BOTÃO"
                    name="ctaLabel"
                    value={form.ctaLabel}
                    onChange={handleChange}
                    fullWidth
                  />
                </Grid>
              )}
              <Grid item xs={12} md={isSimpleHero ? 12 : 6}>
                <TextField
                  label={
                    isSimpleHero ? "Link do banner (URL completa)" : "LINK DO BOTÃO"
                  }
                  name="ctaHref"
                  value={form.ctaHref}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              <Grid item xs={12} md={8}>
                <TextField
                  label="URL da Imagem"
                  name="imageSrc"
                  value={form.imageSrc}
                  onChange={handleChange}
                  fullWidth
                  placeholder="Cole uma URL ou selecione um arquivo ao lado"
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={pickFile}
                  startIcon={<AddPhotoAlternateRoundedIcon />}
                >
                  Selecionar arquivo
                </Button>
                <input
                  ref={fileRef}
                  hidden
                  type="file"
                  accept="image/*"
                  onChange={handleFile}
                />
                {form.imageFile && (
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mt: 0.5 }}
                    color="text.secondary"
                  >
                    {form.imageFile.name}
                  </Typography>
                )}
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Alt (acessibilidade)"
                  name="imageAlt"
                  value={form.imageAlt}
                  onChange={handleChange}
                  fullWidth
                />
              </Grid>

              {/* Cor de fundo só faz sentido para hero fragmentado (com textos) */}
              {!isCard && !isSimpleHero && (
                <Grid item xs={12} md={6}>
                  <TextField
                    type="color"
                    label="Cor de fundo"
                    name="background"
                    value={form.background}
                    onChange={handleChange}
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}

              {/* Config específicas de card */}
              {/* {isCard && (
                <>
                  <Grid item xs={12} md={6}>
                    <TextField
                      label="Ratio (ex.: 1175/440)"
                      name="ratio"
                      value={form.ratio}
                      onChange={handleChange}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <FormControl fullWidth>
                      <InputLabel>Object Fit</InputLabel>
                      <Select
                        label="Object Fit"
                        name="fit"
                        value={form.fit}
                        onChange={handleChange}
                      >
                        <MenuItem value="cover">cover</MenuItem>
                        <MenuItem value="contain">contain</MenuItem>
                        <MenuItem value="fill">fill</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </>
              )} */}
            </Grid>
          </Grid>

          {/* Coluna direita (preview) */}
          <Grid item xs={12} md={4.5}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Preview
            </Typography>
            <Preview banner={form} />
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
              As imagens selecionadas fazem <em>upload</em> real para o S3 ao
              salvar. Você também pode colar uma URL pública direta no campo
              &quot;URL da Imagem&quot;.
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {sizeHint}
            </Typography>
          </Grid>
        </Grid>

        {/* Ações */}
        <Box
          sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 3 }}
        >
          <Button
            variant="outlined"
            onClick={onClose}
            disabled={isBusy}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isBusy}
          >
            {isBusy ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Salvar"
            )}
          </Button>
        </Box>
      </form>

      <Dialog
        open={aiDialogOpen}
        onClose={() => {
          if (isGeneratingAi) return;
          setAiDialogOpen(false);
          setAiErrorMessage(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <AutoAwesomeIcon fontSize="small" />
          Gerar banner com IA
        </DialogTitle>
        <DialogContent dividers>
          {aiErrorMessage ? (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAiErrorMessage(null)}>
              {aiErrorMessage}
            </Alert>
          ) : null}

          <Alert severity="info" variant="outlined" sx={{ mb: 2 }}>
            {aiTypeHint} O contexto do workspace (empresa, produtos e banners) é usado automaticamente — revise tudo antes de salvar.
          </Alert>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
            <Chip
              size="small"
              label={
                form.tipo === "heroImage"
                  ? "Hero simples"
                  : form.tipo === "card"
                  ? "Card"
                  : "Carousel"
              }
              variant="outlined"
              color="primary"
            />
            <Chip
              size="small"
              label={aiOutputPreview.label}
              variant="outlined"
            />
            <Chip
              size="small"
              label={`Proporção ${aiOutputPreview.aspectRatio}`}
              variant="outlined"
            />
            <Chip
              size="small"
              label="Sem salvar automaticamente"
              variant="outlined"
            />
          </Box>

          <TextField
            label="Descreva o banner desejado"
            placeholder={aiPlaceholder}
            value={aiBrief}
            onChange={(e) => setAiBrief(e.target.value)}
            fullWidth
            multiline
            minRows={5}
            disabled={isGeneratingAi}
          />

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mt: 1 }}
          >
            Dica: descreva objetivo, público, oferta, tom visual e o que evitar — como em um briefing com um designer. A IA interpreta o pedido, monta o formulário e gera a imagem.
          </Typography>

          {isGeneratingAi && aiProgress ? (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              {aiProgress}
            </Typography>
          ) : null}

          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setAiAdvancedOpen((v) => !v)}
              endIcon={
                <ExpandMoreIcon
                  sx={{
                    transform: aiAdvancedOpen ? "rotate(180deg)" : "rotate(0deg)",
                    transition: "transform 0.2s",
                  }}
                />
              }
              disabled={isGeneratingAi}
            >
              Opções avançadas
            </Button>
            <Collapse in={aiAdvancedOpen}>
              <Box
                sx={{
                  mt: 1.5,
                  p: 1.5,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  bgcolor: "action.hover",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Tamanho do banner
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
                  Defina largura e altura em pixels. A proporção é calculada automaticamente
                  e usada na geração e no preview.
                </Typography>
                <Grid container spacing={1.5} sx={{ mb: 1 }}>
                  <Grid item xs={6}>
                    <TextField
                      label="Largura"
                      type="number"
                      value={aiOutputWidth}
                      onChange={(e) => setAiOutputWidth(e.target.value)}
                      fullWidth
                      size="small"
                      disabled={isGeneratingAi}
                      inputProps={{ min: 400, max: 3840, step: 1 }}
                      InputProps={{
                        endAdornment: (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            px
                          </Typography>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      label="Altura"
                      type="number"
                      value={aiOutputHeight}
                      onChange={(e) => setAiOutputHeight(e.target.value)}
                      fullWidth
                      size="small"
                      disabled={isGeneratingAi}
                      inputProps={{ min: 100, max: 2160, step: 1 }}
                      InputProps={{
                        endAdornment: (
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                            px
                          </Typography>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
                  <Chip
                    size="small"
                    color="primary"
                    variant="outlined"
                    label={`${aiOutputPreview.label} · ${aiOutputPreview.aspectRatio}`}
                  />
                  <Chip
                    size="small"
                    variant="outlined"
                    label={`Ratio ${aiOutputPreview.ratio}`}
                  />
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Imagens para compor no banner
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Logos, produtos ou fotos que a IA deve incorporar na arte (até {MAX_AI_VISUAL_REFS}).
                </Typography>
                <input
                  ref={aiAssetRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    addAiVisualFiles(e.target.files, "asset");
                    e.target.value = "";
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddPhotoAlternateRoundedIcon />}
                  onClick={() => aiAssetRef.current?.click()}
                  disabled={isGeneratingAi || aiReferenceAssets.length >= MAX_AI_VISUAL_REFS}
                  sx={{ mb: 1 }}
                >
                  Adicionar imagens
                </Button>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {aiReferenceAssets.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        position: "relative",
                        width: 88,
                        height: 56,
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={item.preview}
                        alt={item.label}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeAiVisualItem("asset", item.id)}
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bgcolor: "rgba(0,0,0,.45)",
                          color: "white",
                          "&:hover": { bgcolor: "rgba(0,0,0,.6)" },
                        }}
                      >
                        <CloseRoundedIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Banners de exemplo (identidade visual)
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Envie artes de referência para manter paleta, layout e estilo da marca (até {MAX_AI_VISUAL_REFS}).
                </Typography>
                <input
                  ref={aiStyleRef}
                  type="file"
                  accept="image/*"
                  multiple
                  hidden
                  onChange={(e) => {
                    addAiVisualFiles(e.target.files, "style");
                    e.target.value = "";
                  }}
                />
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<AddPhotoAlternateRoundedIcon />}
                  onClick={() => aiStyleRef.current?.click()}
                  disabled={isGeneratingAi || aiStyleExamples.length >= MAX_AI_VISUAL_REFS}
                  sx={{ mb: 1 }}
                >
                  Adicionar exemplos
                </Button>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 2 }}>
                  {aiStyleExamples.map((item) => (
                    <Box
                      key={item.id}
                      sx={{
                        position: "relative",
                        width: 120,
                        height: 48,
                        borderRadius: 1,
                        overflow: "hidden",
                        border: "1px solid",
                        borderColor: "divider",
                      }}
                    >
                      <Box
                        component="img"
                        src={item.preview}
                        alt={item.label}
                        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => removeAiVisualItem("style", item.id)}
                        sx={{
                          position: "absolute",
                          top: 0,
                          right: 0,
                          bgcolor: "rgba(0,0,0,.45)",
                          color: "white",
                          "&:hover": { bgcolor: "rgba(0,0,0,.6)" },
                        }}
                      >
                        <CloseRoundedIcon fontSize="inherit" />
                      </IconButton>
                    </Box>
                  ))}
                </Box>

                <Divider sx={{ my: 1.5 }} />

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={aiUseWorkspaceBanners}
                      onChange={(e) => setAiUseWorkspaceBanners(e.target.checked)}
                      disabled={isGeneratingAi}
                    />
                  }
                  label="Usar banners publicados do workspace como referência de identidade"
                />

                {workspaceBanners.length > 0 ? (
                  <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}>
                    {workspaceBanners.slice(0, 12).map((banner) => {
                      const selected = aiWorkspaceBannerIds.includes(Number(banner.id));
                      return (
                        <Chip
                          key={banner.id}
                          size="small"
                          label={banner.nome || `Banner #${banner.id}`}
                          variant={selected ? "filled" : "outlined"}
                          color={selected ? "primary" : "default"}
                          onClick={() => toggleWorkspaceBannerId(banner.id)}
                          disabled={isGeneratingAi}
                        />
                      );
                    })}
                  </Box>
                ) : (
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Nenhum banner publicado com imagem encontrado no workspace.
                  </Typography>
                )}
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                  Dica: selecione chips específicos ou marque a opção acima para incluir os mais recentes automaticamente.
                </Typography>
              </Box>
            </Collapse>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setAiDialogOpen(false)}
            disabled={isGeneratingAi}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={() => aiDraftMutation.mutate()}
            disabled={isGeneratingAi || aiBrief.trim().length < 10}
            startIcon={
              isGeneratingAi ? null : <AutoAwesomeIcon fontSize="small" />
            }
          >
            {isGeneratingAi ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Gerar rascunho"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default AddBannerForm;
